import { SectionWithVerticalSpacing } from '@insify/components';
import { animated, config, useSpring } from '@react-spring/web';
import { useState } from 'react';

import { Layout } from '../Layout';

import type { LayoutBackProps, LayoutWrapperProps } from '../Layout';
import type React from 'react';

type ForwardProps =
  | {
      isForwardHidden: true;
    }
  | {
      isForwardHidden?: false;
      forwardCopy: string;
      isForwardDisabled?: boolean;
      isSubmitting: boolean;
      onSubmit: React.FormEventHandler<HTMLFormElement>;
    };

type BackProps =
  | {
      isBackHidden: true;
    }
  | ({
      isBackHidden?: false;
    } & LayoutBackProps);

export type WizardLayoutProps = BackProps &
  ForwardProps &
  LayoutWrapperProps & {
    bottomBarExtraContent?: React.ReactNode;
  };

const Wrapper = ({
  children,
  onSubmit,
  className,
}: {
  children?: React.ReactNode;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  className?: string;
}) => {
  if (onSubmit === undefined) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Layout.Form onSubmit={onSubmit} className={className}>
      {children}
    </Layout.Form>
  );
};

export const WizardLayoutForm = (props: WizardLayoutProps) => (
  <Wrapper onSubmit={!props.isForwardHidden ? props.onSubmit : undefined} className={props.className}>
    <Layout.NavContainerDesktop>
      {!props.isBackHidden && <Layout.Back backCopy={props.backCopy} onBack={props.onBack} />}
    </Layout.NavContainerDesktop>
    {props.children}
    {(!props.isBackHidden || !props.isForwardHidden) && (
      <Layout.MobileOnly>
        <Layout.BottomBar extraContent={props.bottomBarExtraContent}>
          {!props.isBackHidden && <Layout.BackSmall backCopy={props.backCopy} onBack={props.onBack} />}
          {!props.isForwardHidden && (
            <Layout.SubmitFullWidth
              submitCopy={props.forwardCopy}
              isLoading={props.isSubmitting}
              isDisabled={props.isForwardDisabled}
            />
          )}
        </Layout.BottomBar>
      </Layout.MobileOnly>
    )}
  </Wrapper>
);

export const WizardLayoutFormWithCommonSection = ({ children, ...props }: WizardLayoutProps) => {
  const hasHiddenForwardButton = !props.isForwardHidden && props.isForwardDisabled;
  const hasVisibleForwardButton = !props.isForwardHidden && !props.isForwardDisabled;
  const [buttonPosition, setButtonPosition] = useState<'sticky' | 'initial'>(
    hasHiddenForwardButton ? 'initial' : 'sticky',
  );

  const transition = useSpring({
    onStart: () => hasVisibleForwardButton && setButtonPosition('sticky'),
    onRest: () => hasHiddenForwardButton && setButtonPosition('initial'),
    bottom: hasHiddenForwardButton ? '-56px' : '64px',
    config: config.stiff,
  });

  return (
    <WizardLayoutForm {...props}>
      <SectionWithVerticalSpacing>
        {children}
        {!props.isForwardHidden && (
          <animated.div style={{ ...transition, position: buttonPosition }}>
            <Layout.SubmitDesktop
              submitCopy={props.forwardCopy}
              isLoading={props.isSubmitting}
              isDisabled={props.isForwardDisabled}
            />
          </animated.div>
        )}
      </SectionWithVerticalSpacing>
    </WizardLayoutForm>
  );
};
